import {
  authenticated,
  setToken,
} from '@core/store/actions/session-auth.actions';
import { Router } from '@angular/router';
import { Injectable, NgZone } from '@angular/core';
import { Store } from '@ngrx/store';
import { runtimeBridgeUpdate } from '@core/store/actions/native-clients.actions';
import { USER_ROLE } from '@core/store/reducers/session-info.reducer';
import { combineLatest, of, ReplaySubject, switchMap } from 'rxjs';
import { MessengerService } from '@core/services/messenger/messenger.service';
import {
  isUserRoleProviderSelector,
  isUserRoleRepresentativeSelector,
} from '@core/store/selectors/user-role.selectors';
import { MatDialog } from '@angular/material/dialog';
import { ExtendedWindow } from '../../core/interfaces/window.interface';
import { TranslocoService } from '@jsverse/transloco';
import { ContentApiService } from '@core/services/api/content-api.service';
import { consentUrlsLoaded } from '@core/store/actions/consent-urls.actions';
import { AUTHENTICATION_TYPE } from '@xt/domain-model';
import { MobileDetectionService } from '@xt/core/mobile';
import { isNativeClient } from '../helpers/native-client.helper';

declare let window: ExtendedWindow;

@Injectable({
  providedIn: 'root',
})
export class NativeClientService {
  private readonly _isNative: boolean;
  _payload: string = null;
  token;
  userRoles: USER_ROLE[] = undefined;
  url: string = null;
  isLoggedIn: boolean = false;

  private _authenticationType?: AUTHENTICATION_TYPE;
  private userRoleAssertions: Map<USER_ROLE, ReplaySubject<boolean>> =
    new Map();

  constructor(
    private router: Router,
    private ngZone: NgZone,
    private store: Store,
    private messengerService: MessengerService,
    private dialog: MatDialog,
    private translocoService: TranslocoService,
    private contentApiService: ContentApiService,
    private mobileDetectionService: MobileDetectionService,
  ) {
    /* 
    Exports Angular functions and makes them available in the window property.
    The native clients can call these exported function.
    */
    this._isNative = isNativeClient();
  }

  ngOnInit() {
    this.translocoService.langChanges$
      .pipe(
        switchMap((language) =>
          this.contentApiService.getConsentUrls(language),
        ),
      )
      .subscribe((consents) => {
        this.store.dispatch(consentUrlsLoaded({ consentUrlsLoaded: consents }));
      });

    const provideUserPayload = this.setRuntimeBridgePayload.bind(this);
    window.provideUserPayload = provideUserPayload;

    const provideMessengerCode = this.setMessengerCode.bind(this);
    window.provideMessengerCode = provideMessengerCode;

    const provideRefreshToken = this.provideRefreshToken.bind(this);
    window.provideRefreshToken = provideRefreshToken;
    const sendMessengerPackage = this.sendMessengerPackage.bind(this);
    window.sendMessengerPackage = sendMessengerPackage;

    const restartMessengerLogin = this.restartMessengerLogin.bind(this);
    window.restartMessengerLogin = restartMessengerLogin;

    const provideMessengerCredentials = this.setMessengerCredentials.bind(this);
    window.provideMessengerCredentials = provideMessengerCredentials;

    const pinProcessCompleted = this.pinProcessCompleted.bind(this);
    window.pinProcessCompleted = pinProcessCompleted;

    const notifyPushRegisterCompleted =
      this.notifyPushRegisterCompleted.bind(this);
    window.notifyPushRegisterCompleted = notifyPushRegisterCompleted;

    window.assertUserRolesSucceeded = this.assertUserRolesSucceeded.bind(this);

    window.backToLogin = this.backToLogin.bind(this);

    const logoutFn = this.logout.bind(this);
    window.logout = logoutFn;

    const json = {
      type: 'webViewReady',
    };
    if (this._isNative) {
      window.alert(JSON.stringify(json));
    }

    return of(true).toPromise();
  }

  /**
   * Returns true for native clients
   *
   * @return boolean
   */
  get isNative() {
    return this._isNative;
  }
  //checks if portal is running under native mobile app or within mobile device in browser
  get isMobileDevice() {
    return this._isNative || this.mobileDetectionService.isMobileDevice;
  }

  /**
   * Sets the provided user payload
   *
   * @param payload - The authentication token payload
   * @param userRoles - Array of user roles to be assigned
   * @param userIdFromToken - Optional user ID extracted from the token
   * @param authenticationType - Optional authentication type
   */
  setRuntimeBridgePayload(
    payload: string,
    userRoles: USER_ROLE[],
    userIdFromToken?: any,
    authenticationType?: any,
  ) {
    if (payload != null && payload != '' && payload != undefined) {
      this._payload = payload;

      this.store.dispatch(authenticated({ token: payload }));
      this.store.dispatch(setToken({ token: payload }));
      this.userRoles = userRoles;
      this.store.dispatch(
        runtimeBridgeUpdate({
          user_roles: userRoles,
          authentication_type: authenticationType,
        }),
      );
      this._authenticationType = authenticationType;

      // Check if any of the user roles has a pending assertion
      const scopeAssertion$ = userRoles
        .map((role) => this.userRoleAssertions.get(role))
        .find((assertion) => assertion !== undefined);

      // Check if user has HCP or PATIENT role
      const hasHcpOrPatientRole = userRoles.some((role) =>
        [USER_ROLE.HCP, USER_ROLE.PATIENT].includes(role),
      );

      if (hasHcpOrPatientRole) {
        this.messengerService.isStarted = false;
        this.messengerService.setLoginToken(payload);
        this.messengerService.setUserSubId(userIdFromToken);
        setTimeout(() => {
          this.messengerService.startMessenger();
        }, 200);
        this.router.navigate(['dashboard']);
      }

      if (scopeAssertion$) {
        // New Token after scope assertion / elevation
        scopeAssertion$.next(true);
        scopeAssertion$.complete();

        const currentUrl = this.router.url;
        this.router
          .navigateByUrl('/', { skipLocationChange: true })
          .then(() => {
            this.router.navigate([currentUrl]);
          });
      } else {
        // First Token after initial login / startup
        this.router.navigate(['dashboard']);

        setTimeout(() => {
          if (hasHcpOrPatientRole) {
            this.messengerService.startMessenger();
          }
        }, 1000);
      }
    }

    this.isLoggedIn = true;

    setTimeout(() => {
      this.isLoggedIn = false;
    }, 20000);
  }

  setMessengerCode(code, status, issuer) {
    console.debug('CLIENT: in setMessengerCode');
    this.messengerService.setMessengerCode(code, status, issuer);
  }

  pinProcessCompleted() {
    this.dialog.closeAll();
    this.fetchNewToken();
    this.store.dispatch(
      runtimeBridgeUpdate({
        user_roles: [USER_ROLE.PATIENT, USER_ROLE.PRIVATE_RECORD],
        // pin linking must be internal
        authentication_type: AUTHENTICATION_TYPE.INTERNAL,
      }),
    );
    this.assertUserRolesScopes([USER_ROLE.PATIENT]);
    this.router.navigate(['dashboard']);
  }

  sendMessengerPackage(index) {
    const base64Messenger = {
      type: 'messengerPackage',
      data: this.messengerService.chatExportArray[index],
    };

    window.alert(JSON.stringify(base64Messenger));
  }

  fetchNewToken() {
    console.debug('CLIENT: in fetchNewToken');
    const newTokenNeeded = {
      type: 'fetchNewToken',
    };

    window.alert(JSON.stringify(newTokenNeeded));
  }

  nativeLogin() {
    console.debug('CLIENT: in nativeLogin');
    const nativeLogin = {
      type: 'login',
    };

    window.alert(JSON.stringify(nativeLogin));
  }

  provideRefreshToken(token) {
    console.debug('CLIENT: new refresh Token ', token);
    this._payload = token;
    this.store.dispatch(authenticated({ token }));
    this.store.dispatch(setToken({ token }));
    this.messengerService.setMessengerToken(token);
  }

  notifyPushRegisterCompleted() {
    console.debug('CLIENT: push reg completed');
    const provider$ = this.store.select(isUserRoleProviderSelector);
    const representative$ = this.store.select(isUserRoleRepresentativeSelector);

    combineLatest([provider$, representative$]).subscribe(
      ([provider, representative]) => {
        let json = {};
        if (provider) {
          json = {
            type: 'pushNotifications',
            data: [
              {
                eventTypeName: 'messages',
                subscription: true,
              },
              {
                eventTypeName: 'appointments',
                subscription: true,
              },
              {
                eventTypeName: 'documents',
                subscription: true,
              },
              {
                eventTypeName: 'logbook',
                subscription: true,
              },
              {
                eventTypeName: 'cancellation',
                subscription: true,
              },
            ],
          };
        } else if (representative) {
          json = {
            type: 'pushNotifications',
            data: [
              {
                eventTypeName: 'messages',
                subscription: true,
              },
              {
                eventTypeName: 'appointments',
                subscription: true,
              },
              {
                eventTypeName: 'documents',
                subscription: true,
              },
              {
                eventTypeName: 'transition',
                subscription: true,
              },
              {
                eventTypeName: 'questionnaires',
                subscription: true,
              },
              {
                eventTypeName: 'authorizations',
                subscription: true,
              },
            ],
          };
        } else {
          // isPatient
          json = {
            type: 'pushNotifications',
            data: [
              {
                eventTypeName: 'messages',
                subscription: true,
              },
              {
                eventTypeName: 'appointments',
                subscription: true,
              },
              {
                eventTypeName: 'documents',
                subscription: true,
              },
              {
                eventTypeName: 'transition',
                subscription: true,
              },
              {
                eventTypeName: 'questionnaires',
                subscription: true,
              },
            ],
          };
        }
        window.alert(JSON.stringify(json));
      },
    );
  }

  restartMessengerLogin() {
    console.debug('CLIENT: in restartMessengerLogin');
    if (this.userRoles.length != 1) {
      this.messengerService.isStarted = false;
      this.messengerService.startMessenger();
    }
  }

  setMessengerCredentials(matrixId, databaseSecret) {
    console.debug('CLIENT: in setMatrixID');
    console.debug('CLIENT:', matrixId);
    console.debug('CLIENT:', databaseSecret);
    this.messengerService.setMatrixId(matrixId);
    this.messengerService.setDatabaseSecret(databaseSecret);
  }

  // logout sent from native to web
  logout() {
    this._payload = null;
    this._authenticationType = undefined;
    const user_role = null;
    this.store.dispatch(setToken({ token: null }));
    this.store.dispatch(runtimeBridgeUpdate({ user_roles: user_role }));

    this.ngZone.run(() => {
      this.router.navigate(['/']);
    });
  }

  assertUserRolesScopes(roles: USER_ROLE[], url?: string) {
    console.debug('CLIENT: assert user role, ', roles);
    console.debug('CLIENT: current user role, ', this.userRoles);
    console.debug('CLIENT: assertUserRoleScopes url, ', url);

    this.url = url;

    for (const currentRole of roles) {
      const assertion$ = new ReplaySubject<boolean>(1);
      this.userRoleAssertions.set(currentRole, assertion$);

      console.debug('CLIENT: before user role alert', currentRole);
    }

    alert(
      JSON.stringify({
        type: 'assertUserRoles',
        data: roles,
      }),
    );

    // Fallback if no assertion found (shouldn't happen)
    return of(roles);
  }

  assertUserRolesSucceeded(roles: USER_ROLE[], token?: string) {
    for (const currentRole of roles) {
      const assertion$ = this.userRoleAssertions.get(currentRole);
      assertion$?.next(true);
      assertion$?.complete();
    }

    console.debug('CLIENT: ASSERTUSERROLESUCCEEDED TOKEN', token);

    if (token) {
      console.debug('CLIENT: ASSERTUSERROLESUCCEEDED _payload', this._payload);
      this._payload = token;

      this.store.dispatch(authenticated({ token }));
    }

    console.debug('CLIENT: in asserUserRoleSucceeded', this.userRoles, roles);
    if (this.userRoles != roles) {
      this.userRoles = roles;
    }

    if (this.url !== null && this.url !== undefined) {
      console.debug(
        'CLIENT: assertUserRoleScopes url before navigate, ',
        this.url,
      );
      this.router.navigate([this.url]);
      console.debug(
        'CLIENT: assertUserRoleScopes url after navigate, ',
        this.url,
      );
      this.url = null;
    }
  }

  /**
   * Redirect to the native login page will happen
   */
  backToLogin() {
    alert(
      JSON.stringify({
        type: 'backToLogin',
      }),
    );
  }

  /**
   * get function to recieve header Values
   *
   * @return header values
   */
  get headerValues(): string {
    return this._payload;
  }

  get payload() {
    return this._payload;
  }

  get authenticationType(): AUTHENTICATION_TYPE | undefined {
    return this._authenticationType;
  }
}
